<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiButton, UiChip, UiText } from '@shopexperts/shared';
import LinkGroupsDialog from './LinkGroupsDialog.vue';
import { countLinkGroupLinks, parseLinkGroupsValue } from '../../utils/linkGroups';

const props = defineProps<{
  name: string;
  hint?: string;
}>();

const model = defineModel<string>({ default: '' });

const isDialogOpen = ref(false);

const groups = computed(() => parseLinkGroupsValue(model.value));
const linkCount = computed(() => countLinkGroupLinks(groups.value));

function clearAll() {
  model.value = '';
}

function onApply(value: string) {
  model.value = value;
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-col gap-2 rounded-lg border border-se-gray-200 p-3">
      <div v-if="groups.length" class="flex flex-wrap items-center gap-1.5">
        <UiChip
          v-for="(group, index) in groups"
          :key="index"
          :text="`${group.heading || `Group ${index + 1}`} (${group.links.filter((link) => link.label || link.href).length})`"
          size="tag"
          variant="outline"
        />
      </div>
      <UiText
        v-else
        as="p"
        variant="caption-normal"
        class="text-se-gray-500"
        text="No link groups yet — the website section stays hidden on this page."
      />
      <div class="flex items-center gap-2">
        <UiButton
          :label="groups.length ? `Edit links (${linkCount})` : 'Add link groups'"
          variant="outline"
          size="sm"
          :name="`${props.name}-edit-link-groups`"
          @click="isDialogOpen = true"
        />
        <UiButton
          v-if="groups.length"
          label="Clear all"
          variant="ghost"
          size="sm"
          hover-style="highlight"
          @click="clearAll"
        />
      </div>
    </div>

    <UiText v-if="props.hint" as="p" variant="caption-normal" class="text-se-gray-500" :text="props.hint" />

    <!-- v-if unmounts the teleported dialog DOM on close; the shared UiDialog
         lazy-mounts but never unmounts, which leaves a full-screen overlay. -->
    <LinkGroupsDialog v-if="isDialogOpen" v-model:open="isDialogOpen" :value="model" @apply="onApply" />
  </div>
</template>
